import jsPDF from 'jspdf';
import moment from 'moment';
import { Organisation } from '@common/types/objects';
import i18n from '../../../i18n';

export interface ExportCertificatePDFOptions {
  courseName: string;
  employeeName: string;
  completionDate: string;
  currentPoints: number;
  maxPoints: number;
  org: Organisation;
  /** Pre-loaded org logo as base64 to avoid CORS issues */
  preloadedOrgLogoBase64?: string | null;
}

const ONETEAM_LOGO = '/static/images/logo_large.png';
const DEFAULT_PRIMARY_COLOR = '#2563EB';

// Generate SVG with primary color as background and lighter decorative shapes
function generateBackgroundSVG(primaryColor: string): string {
  // Generate unique IDs to avoid conflicts when multiple certificates are rendered
  const uniqueId = Math.random().toString(36).substring(2, 9);
  const clipId = `clip0_${uniqueId}`;
  const gradient0Id = `paint0_linear_${uniqueId}`;
  const gradient1Id = `paint1_linear_${uniqueId}`;
  const gradient2Id = `paint2_linear_${uniqueId}`;

  // SVG with solid background + white/light decorative shapes that fade to transparent
  // This creates the effect of lighter shapes on a colored background
  const path1 = 'M-19.5 642.045C102.831 642.045 202 741.214 202 863.545C202 985.876 102.831 1085.05 '
    + '-19.5 1085.05C-141.831 1085.05 -241 985.876 -241 863.545C-241 741.214 -141.831 642.045 -19.5 642.045Z';
  const path2 = 'M-211.324 -174.205C-150.159 -280.147 -14.6917 -316.445 91.25 -255.279C197.192 -194.114 '
    + '233.49 -58.6468 172.325 47.2949C111.16 153.237 -24.3082 189.536 -130.25 128.37C-236.192 67.2045 '
    + '-272.49 -68.2633 -211.324 -174.205Z';

  return `<svg width="192" height="649" viewBox="0 0 192 649" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="192" height="649" fill="${primaryColor}"/>
<g clip-path="url(#${clipId})" opacity="0.3">
<path d="${path1}" stroke="url(#${gradient0Id})" stroke-width="100"/>
<path d="${path2}" stroke="url(#${gradient1Id})" stroke-width="100"/>
<circle cx="217.5" cy="400.5" r="153.5" stroke="url(#${gradient2Id})" stroke-width="100"/>
</g>
<defs>
<linearGradient id="${gradient0Id}" x1="-291" y1="863.545" x2="252" y2="863.545" gradientUnits="userSpaceOnUse">
<stop stop-color="white"/>
<stop offset="1" stop-color="white" stop-opacity="0"/>
</linearGradient>
<linearGradient id="${gradient1Id}" x1="116.25" y1="-298.581" x2="-155.25" y2="171.671" gradientUnits="userSpaceOnUse">
<stop stop-color="white"/>
<stop offset="1" stop-color="white" stop-opacity="0"/>
</linearGradient>
<linearGradient id="${gradient2Id}" x1="217.5" y1="197" x2="217.5" y2="604" gradientUnits="userSpaceOnUse">
<stop stop-color="white"/>
<stop offset="1" stop-color="white" stop-opacity="0"/>
</linearGradient>
<clipPath id="${clipId}">
<rect width="192" height="800" fill="white"/>
</clipPath>
</defs>
</svg>`;
}

// Convert SVG string to data URL
function svgToDataUrl(svgString: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svgString)}`;
}

function generateBadgeSVG(
  primaryColor: string,
  isWhitelabel: boolean,
  orgLogoUrl: string | null
): string {
  const color = primaryColor || DEFAULT_PRIMARY_COLOR;
  const cx = 76.4261;
  const cy = 76.4271;

  // Use org logo if whitelabel and logo is available (works with data URL or regular URL)
  const centerLogoContent = isWhitelabel && orgLogoUrl
    ? `<image href="${orgLogoUrl}" x="${cx - 26}" y="${cy - 26}" width="52" height="52" `
      + 'preserveAspectRatio="xMidYMid slice" clip-path="url(#circleClipBadge)"/>'
    : '<path d="M101.364 90.9919C103.867 86.7161 105.301 81.7391 105.301 76.4271C105.301 60.4799 '
      + '92.3734 47.5521 76.4261 47.5521C60.4789 47.5521 47.5511 60.4799 47.5511 76.4271C47.5511 81.7182 '
      + '48.9743 86.6769 51.4586 90.9413C59.7999 82.287 63.1396 73.2521 66.6645 63.5676C68.6098 58.2227 '
      + '74.5197 55.4669 79.8645 57.4123C85.2093 59.3576 87.9651 65.2675 86.0198 70.6123L85.9403 70.8306'
      + 'C82.3787 80.6171 78.0857 92.4131 67.5319 103.906C70.3336 104.813 73.3227 105.302 76.4261 105.302'
      + 'C79.5057 105.302 82.4727 104.82 85.2557 103.927C83.5792 102.103 82.0605 100.271 80.6775 98.4402'
      + 'C80.1966 97.8036 80.1787 96.9353 80.6032 96.2599C84.8419 89.5171 87.5794 82.9707 89.7781 77.1231'
      + 'C90.0808 76.318 91.2179 76.2716 91.6013 77.0417C93.9765 81.8124 96.9885 86.4586 101.364 90.9919Z" '
      + 'fill="white"/>';

  const oneTeamLogo = i18n.t('common:one_team_logo', 'oneteam');

  const badgeText = !isWhitelabel
    ? oneTeamLogo + ' ' + i18n.t('learning:certificate_of_completion', 'Certificate of completion')
    : i18n.t('learning:certificate_of_completion', 'Certificate of completion');
  const textRadius = 54;
  const chars = badgeText.split('');
  const totalAngle = 280;

  // Calculate adaptive font size based on text length
  // Base: English "ONETEAM CERTIFICATE OF COMPLETION" = ~34 chars fits at fontSize 8
  const baseTextLength = 34;
  const baseFontSize = 8;
  const nonSpaceChars = badgeText.replace(/\s/g, '').length;
  const scaleFactor = Math.min(1, baseTextLength / nonSpaceChars);
  const adaptiveFontSize = Math.max(5, baseFontSize * scaleFactor); // Min font size of 5

  // Calculate if there's a gap (short text doesn't fill circle)
  // If text is short, position gap at bottom (start at 130°); if text is long, start at -140°
  const hasGap = nonSpaceChars < 40; // Approximate threshold for when text fills the circle
  const startAngle = hasGap ? 130 : -140;
  const anglePerChar = totalAngle / (chars.length - 1);

  let textElements = '';
  chars.forEach((char, idx) => {
    const upperChar = char.toUpperCase();
    if (upperChar === ' ') return;
    const angle = startAngle + (idx * anglePerChar);
    const rad = (angle * Math.PI) / 180;
    const x = cx + textRadius * Math.cos(rad);
    const y = cy + textRadius * Math.sin(rad);
    const rotation = angle + 90;
    textElements += `<text x="${x.toFixed(2)}" y="${y.toFixed(2)}" fill="#71717A" `
      + `font-size="${adaptiveFontSize.toFixed(1)}" `
      + 'font-weight="600" font-family="Arial,sans-serif" text-anchor="middle" dominant-baseline="middle" '
      + `transform="rotate(${rotation.toFixed(2)},${x.toFixed(2)},${y.toFixed(2)})">${upperChar}</text>`;
  });

  const badgePath = 'M70.3334 4.47789C73.9368 1.17444 79.4673 1.17438 83.0707 4.47789C84.7523 6.01963 87.2191 6.34419 '
    + '89.2425 5.29039C93.5782 3.03199 98.9216 4.46359 101.547 8.58727C102.773 10.5116 105.071 11.4635 107.298 10.9691'
    + 'C112.071 9.90982 116.861 12.6757 118.329 17.3382C119.015 19.5144 120.989 21.0293 123.269 21.1283C128.153 21.3405'
    + ' 132.064 25.2513 132.276 30.1351C132.375 32.4145 133.89 34.3881 136.066 35.0736C140.728 36.5425 143.493 41.3334'
    + ' 142.434 46.1058C141.94 48.333 142.892 50.6315 144.817 51.8568C148.94 54.4823 150.372 59.8249 148.114 64.1605'
    + 'C147.06 66.184 147.384 68.6516 148.926 70.3334C152.229 73.9367 152.23 79.4673 148.926 83.0707C147.384 84.7523'
    + ' 147.06 87.2191 148.114 89.2425C150.372 93.5782 148.94 98.9216 144.817 101.547C142.892 102.773 141.94 105.071'
    + ' 142.434 107.298C143.493 112.071 140.728 116.86 136.066 118.329C133.89 119.015 132.375 120.99 132.276 123.269'
    + 'C132.064 128.153 128.153 132.064 123.269 132.276C120.99 132.375 119.015 133.89 118.329 136.066C116.86 140.728'
    + ' 112.071 143.493 107.298 142.434C105.071 141.94 102.773 142.892 101.547 144.817C98.9216 148.94 93.5782 150.372'
    + ' 89.2425 148.114C87.2191 147.06 84.7523 147.384 83.0707 148.926C79.4673 152.23 73.9367 152.229 70.3334 148.926'
    + 'C68.6516 147.384 66.184 147.06 64.1605 148.114C59.8249 150.372 54.4823 148.94 51.8568 144.817C50.6315 142.892'
    + ' 48.333 141.94 46.1058 142.434C41.3334 143.493 36.5425 140.728 35.0736 136.066C34.3881 133.89 32.4145 132.375'
    + ' 30.1351 132.276C25.2513 132.064 21.3405 128.153 21.1283 123.269C21.0293 120.989 19.5144 119.015 17.3382 118.329'
    + 'C12.6757 116.861 9.90982 112.071 10.9691 107.298C11.4635 105.071 10.5116 102.773 8.58727 101.547C4.46359 98.9216'
    + ' 3.03199 93.5782 5.29039 89.2425C6.34419 87.2191 6.01963 84.7523 4.47789 83.0707C1.17438 79.4673 1.17444 73.9368'
    + ' 4.47789 70.3334C6.01972 68.6516 6.34441 66.184 5.29039 64.1605C3.03218 59.8249 4.46368 54.4823 8.58727 51.8568'
    + 'C10.5117 50.6314 11.4634 48.333 10.9691 46.1058C9.90981 41.3334 12.6755 36.5425 17.3382 35.0736C19.5144 34.3881'
    + ' 21.0293 32.4145 21.1283 30.1351C21.3404 25.2512 25.2512 21.3404 30.1351 21.1283C32.4145 21.0293 34.3881 19.5144'
    + ' 35.0736 17.3382C36.5425 12.6755 41.3334 9.90981 46.1058 10.9691C48.333 11.4634 50.6314 10.5117 51.8568 8.58727'
    + 'C54.4823 4.46368 59.8249 3.03218 64.1605 5.29039C66.184 6.34441 68.6516 6.01972 70.3334 4.47789Z';

  const svgNs = 'http://www.w3.org/2000/svg';
  const xlinkNs = 'http://www.w3.org/1999/xlink';

  return `<svg width="150" height="186" viewBox="0 0 154 189" xmlns="${svgNs}" xmlns:xlink="${xlinkNs}">
    <defs>
      <clipPath id="circleClipBadge">
        <circle cx="${cx}" cy="${cy}" r="26"/>
      </clipPath>
    </defs>
    <path d="M37.3519 133.202H66.3519V188.202L51.8519 173.202L37.3519 188.202V133.202Z" fill="${color}"/>
    <path d="M86.3519 133.202H115.352V188.202L100.852 173.202L86.3519 188.202V133.202Z" fill="${color}"/>
    <path d="${badgePath}" fill="#030712" stroke="white" stroke-width="4"/>
    <circle cx="${cx}" cy="${cy}" r="68" fill="white"/>
    ${textElements}
    <circle cx="${cx}" cy="${cy}" r="40.8375" fill="${color}" stroke="#030712" stroke-width="2.475"/>
    ${centerLogoContent}
    <rect x="9.60153" y="9.60201" width="133.65" height="133.65" rx="66.825" stroke="#030712" stroke-width="3.3" fill="none"/>
  </svg>`;
}

// Load image with specific referrer policy and convert to data URL
async function loadImageWithReferrer(
  url: string,
  referrerPolicy: ReferrerPolicy
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.referrerPolicy = referrerPolicy;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width || 200;
      canvas.height = img.naturalHeight || img.height || 200;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        try {
          const dataURL = canvas.toDataURL('image/png');
          resolve(dataURL);
        } catch {
          reject(new Error('Canvas tainted - CORS blocked'));
        }
      } else {
        reject(new Error('Failed to get canvas context'));
      }
    };

    img.onerror = () => reject(new Error('Image load failed'));
    img.src = url;
  });
}

// Convert an external image URL to a data URL using SVG wrapper technique
// This bypasses CORS by embedding the image in an SVG and rendering it to canvas
async function imageUrlToDataUrlViaSvg(
  url: string,
  width: number = 200,
  height: number = 200
): Promise<string> {
  const svgNs = 'http://www.w3.org/2000/svg';
  const xlinkNs = 'http://www.w3.org/1999/xlink';
  const svgString = `<svg xmlns="${svgNs}" xmlns:xlink="${xlinkNs}" width="${width}" height="${height}">`
    + `<image href="${url}" width="${width}" height="${height}" preserveAspectRatio="xMidYMid meet"/>`
    + '</svg>';

  return new Promise((resolve, reject) => {
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const blobUrl = URL.createObjectURL(svgBlob);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
        try {
          const dataURL = canvas.toDataURL('image/png');
          URL.revokeObjectURL(blobUrl);
          resolve(dataURL);
        } catch (e) {
          URL.revokeObjectURL(blobUrl);
          reject(new Error('Failed to convert to data URL'));
        }
      } else {
        URL.revokeObjectURL(blobUrl);
        reject(new Error('Failed to get canvas context'));
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error('Failed to load SVG wrapper'));
    };

    img.src = blobUrl;
  });
}

// Load image via hidden DOM img element (uses browser's native image loading)
// This can bypass some CORS restrictions because <img> tags load images differently
async function loadImageViaDomElement(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    // Create a hidden img element in the DOM
    const img = document.createElement('img');
    img.style.cssText = 'position:fixed;left:-9999px;top:-9999px;visibility:hidden;';
    img.crossOrigin = 'anonymous';

    const cleanup = () => {
      if (img.parentNode) {
        document.body.removeChild(img);
      }
    };

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Image load timeout'));
    }, 10000);

    img.onload = () => {
      clearTimeout(timeout);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width || 200;
        canvas.height = img.naturalHeight || img.height || 200;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL('image/png');
          cleanup();
          resolve(dataURL);
        } else {
          cleanup();
          reject(new Error('Failed to get canvas context'));
        }
      } catch (e) {
        cleanup();
        reject(new Error('Canvas tainted - CORS blocked'));
      }
    };

    img.onerror = () => {
      clearTimeout(timeout);
      cleanup();
      reject(new Error('Image failed to load'));
    };

    // Add to DOM first, then set src
    document.body.appendChild(img);
    img.src = url;
  });
}

// Convert an external image URL to a data URL
// Tries multiple methods - prioritizes methods that work without strict CORS
async function imageUrlToDataUrl(url: string): Promise<string> {
  // If already a data URL, return as-is
  if (url.startsWith('data:')) {
    return url;
  }

  // Get the current site URL for referrer
  const siteUrl = typeof window !== 'undefined' ? window.location.href : '';
  const siteOrigin = typeof window !== 'undefined' ? window.location.origin : '';

  // Method 1: Load via DOM img element first (most compatible, uses browser's native loading)
  // This works because browsers load images for <img> tags without strict CORS
  // If the image loads and canvas drawing works, we get a data URL
  try {
    const result = await loadImageViaDomElement(url);
    if (result && result.startsWith('data:')) {
      return result;
    }
  } catch {
    // Continue to next method
  }

  // Method 2: SVG wrapper technique (works for badge, should work for logo)
  // SVG's <image> element can load external images with different CORS handling
  try {
    const result = await imageUrlToDataUrlViaSvg(url, 200, 200);
    if (result && result.startsWith('data:')) {
      return result;
    }
  } catch {
    // Continue to next method
  }

  // Method 3: Try fetch with explicit site URL as referrer
  try {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'include',
      referrer: siteUrl,
      referrerPolicy: 'unsafe-url',
    });

    if (response.ok) {
      const blob = await response.blob();
      const result = await blobToDataUrl(blob);
      if (result) return result;
    }
  } catch {
    // Continue to next method
  }

  // Method 4: Try fetch with origin as referrer
  try {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'include',
      referrer: siteOrigin,
      referrerPolicy: 'origin',
    });

    if (response.ok) {
      const blob = await response.blob();
      const result = await blobToDataUrl(blob);
      if (result) return result;
    }
  } catch {
    // Continue to next method
  }

  // Method 5: Try Image element with different referrer policies
  // Need sequential execution to return early on success
  // eslint-disable-next-line no-await-in-loop
  for (const policy of ['unsafe-url', 'no-referrer-when-downgrade', 'origin'] as ReferrerPolicy[]) {
    try {
      const result = await loadImageWithReferrer(url, policy); // eslint-disable-line no-await-in-loop
      if (result && result.startsWith('data:')) {
        return result;
      }
    } catch {
      // Continue to next policy
    }
  }

  // All methods failed
  throw new Error('All image conversion methods failed');
}

// Helper to convert blob to data URL
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert blob to data URL'));
      }
    };
    reader.onerror = () => reject(new Error('FileReader error'));
    reader.readAsDataURL(blob);
  });
}

// Convert SVG to image element and wait for it to load
async function svgToImage(svgString: string, width?: number, height?: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width || img.naturalWidth || 150;
      canvas.height = height || img.naturalHeight || 186;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataURL = canvas.toDataURL('image/png');
        URL.revokeObjectURL(url);
        resolve(dataURL);
      } else {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to get canvas context'));
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load SVG image'));
    };

    img.src = url;
  });
}

interface GenerateCertificateHTMLOptions {
  options: ExportCertificatePDFOptions;
  badgeImageDataURL?: string;
  orgLogoDataURL?: string;
  backgroundImageDataURL?: string;
}

function generateCertificateHTML({
  options,
  badgeImageDataURL,
  orgLogoDataURL,
  backgroundImageDataURL,
}: GenerateCertificateHTMLOptions): string {
  const {
    courseName, employeeName, completionDate, currentPoints, maxPoints, org
  } = options;
  const percentage = maxPoints > 0 ? Math.round((currentPoints / maxPoints) * 100) : 0;
  const formattedDate = completionDate ? moment(completionDate).format('D MMMM, YYYY') : '';

  const primaryColor = org.theme?.main_color || DEFAULT_PRIMARY_COLOR;
  const isWhitelabel = !!org.whitelabel_config;

  const t = (key: string, fallback: string) => i18n.t(key, fallback);
  const escapeHtml = (text: string): string => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // Determine which logo to show at the top
  // Only use data URLs to avoid CORS issues with html2canvas
  // For whitelabel: only show logo if we have a valid data URL (starts with 'data:')
  // For non-whitelabel: show OneTeam logo (local static file, no CORS issue)
  let logoHtml = '';
  if (isWhitelabel) {
    // Only show the logo if it's a valid data URL (conversion succeeded)
    if (orgLogoDataURL && orgLogoDataURL.startsWith('data:')) {
      logoHtml = `<img src="${orgLogoDataURL}" style="height:40px;object-fit:contain;display:block;visibility:hidden;" />`;
    }
    // If conversion failed, don't show broken image - leave empty
  } else {
    logoHtml = `<img src="${ONETEAM_LOGO}" crossorigin="anonymous" style="height:40px;display:block;" />`;
  }

  // Use pre-rendered badge image data URL
  const badgeHtml = badgeImageDataURL
    ? `<img src="${badgeImageDataURL}" style="width:150px;height:186px;display:block;" />`
    : '';

  const leftPanelStyle = 'width:192px;height:650px;position:relative;flex-shrink:0;overflow:hidden;';
  // Use pre-rendered background image data URL (converted from SVG) - no blend mode needed
  const leftImageStyle = 'width:192px;height:650px;object-fit:cover;object-position:left;'
    + 'position:absolute;top:0;left:0;display:block;';
  const mainContentStyle = 'flex:1;background-color:#ffffff;padding:48px;display:flex;flex-direction:row;';
  const leftColumnStyle = 'display:flex;flex-direction:column;justify-content:space-between;flex:1;height:100%;';
  const certTitleStyle = 'color:#6b7280;font-size:20px;font-weight:600;text-transform:uppercase;'
    + 'letter-spacing:2px;margin-bottom:12px;';
  const courseNameStyle = 'font-size:72px;font-weight:500;color:#000000;line-height:1.1;';
  const presentedToStyle = 'color:#6b7280;font-size:20px;font-weight:600;text-transform:uppercase;'
    + 'letter-spacing:2px;margin-bottom:12px;';
  const employeeNameStyle = 'font-size:54px;font-weight:500;color:#000000;line-height:1.1;margin-bottom:12px;';
  const descriptionStyle = 'font-size:24px;font-weight:500;color:#6b7280;line-height:1.5;';
  const badgeContainerStyle = 'width:220px;flex-shrink:0;display:flex;align-items:flex-start;'
    + 'justify-content:flex-end;padding-top:0;';

  const certTitle = escapeHtml(t('learning:certificate_of_completion', 'Certificate of completion'));
  const presentedTo = escapeHtml(t('learning:proudly_presented_to', 'Proudly presented to'));
  const forCompleting = escapeHtml(
    t('learning:for_successfully_completing_the_course_on', 'For successfully completing the course on')
  );
  const achievingScore = escapeHtml(t('learning:achieving_a_score_of', 'achieving a score of'));

  // Use background image data URL if available, otherwise fallback to SVG data URL
  const backgroundSrc = backgroundImageDataURL || svgToDataUrl(generateBackgroundSVG(primaryColor));

  return '<div id="cert-pdf-content" '
    + 'style="width:1200px;height:650px;background-color:#ffffff;display:flex;'
    + 'font-family:Arial,Helvetica,sans-serif;overflow:hidden;">'
    + `<div style="${leftPanelStyle}">`
    + `<img src="${backgroundSrc}" style="${leftImageStyle}" />`
    + '</div>'
    + `<div style="${mainContentStyle}">`
    + `<div style="${leftColumnStyle}">`
    + `<div style="height:40px;">${logoHtml}</div>`
    + '<div>'
    + `<div style="${certTitleStyle}">${certTitle}</div>`
    + `<div style="${courseNameStyle}">${escapeHtml(courseName)}</div>`
    + '</div>'
    + '<div>'
    + `<div style="${presentedToStyle}">${presentedTo}</div>`
    + `<div style="${employeeNameStyle}">${escapeHtml(employeeName)}</div>`
    + `<div style="${descriptionStyle}">`
    + `${forCompleting} <strong style="color:#000000;font-weight:700;">${escapeHtml(formattedDate)}</strong>, `
    + `${achievingScore} <strong style="color:#000000;font-weight:700;">${currentPoints}/${maxPoints} (${percentage}%).</strong>`
    + '</div>'
    + '</div>'
    + '</div>'
    + `<div style="${badgeContainerStyle}">${badgeHtml}</div>`
    + '</div>'
    + '</div>';
}

export async function exportCertificatePDF(options: ExportCertificatePDFOptions): Promise<void> {
  const { courseName, employeeName, org, preloadedOrgLogoBase64 } = options;

  // Convert badge SVG to image data URL first
  const primaryColor = org.theme?.main_color || DEFAULT_PRIMARY_COLOR;
  const isWhitelabel = !!org.whitelabel_config;
  const orgLogo = org.brand_icon;

  // Use pre-loaded base64 if available (from certificate viewer)
  // This bypasses CORS issues since the image was already converted in the view
  let orgLogoDataURL: string | null = null;
  let orgLogoForBadge: string | null = null;

  if (isWhitelabel && orgLogo) {
    // First, check if we have a pre-loaded base64 from the certificate viewer
    if (preloadedOrgLogoBase64 && preloadedOrgLogoBase64.startsWith('data:')) {
      orgLogoDataURL = preloadedOrgLogoBase64;
      orgLogoForBadge = preloadedOrgLogoBase64;
    } else {
      // Fallback: try to convert now
      try {
        orgLogoDataURL = await imageUrlToDataUrl(orgLogo);
        // Verify it's a valid data URL
        if (!orgLogoDataURL.startsWith('data:')) {
          throw new Error('Did not produce a valid data URL');
        }
        orgLogoForBadge = orgLogoDataURL;
      } catch (error) {
        console.warn('Direct conversion failed, will try via badge approach:', error);
        // For badge: use original URL (SVG can handle it)
        orgLogoForBadge = orgLogo;
        // orgLogoDataURL stays null - we'll try to extract from badge later
      }
    }
  }

  // Generate badge SVG with the logo (data URL or original URL)
  const badgeSVG = generateBadgeSVG(primaryColor, isWhitelabel, orgLogoForBadge);

  // Generate background SVG with primary color
  const backgroundSVG = generateBackgroundSVG(primaryColor);

  // Convert badge SVG to image - this embeds all content including the logo
  let badgeImageDataURL: string;
  let backgroundImageDataURL: string;
  try {
    badgeImageDataURL = await svgToImage(badgeSVG);
    // Convert background SVG to image for better PDF compatibility
    backgroundImageDataURL = await svgToImage(backgroundSVG, 192, 649);

    // If we didn't get a data URL for the top logo but badge conversion worked,
    // the SVG technique worked. Try converting the logo via SVG one more time
    // now that the image might be cached by the browser
    if (isWhitelabel && orgLogo && (!orgLogoDataURL || !orgLogoDataURL.startsWith('data:'))) {
      // Small delay to ensure browser has cached the image
      await new Promise((r) => setTimeout(r, 100));
      try {
        // Try again - browser may have cached the image now
        const retryResult = await imageUrlToDataUrlViaSvg(orgLogo, 160, 160);
        if (retryResult && retryResult.startsWith('data:')) {
          orgLogoDataURL = retryResult;
        }
      } catch {
        // Still failed - leave orgLogoDataURL as is
      }
    }
  } catch (error) {
    console.error('Failed to convert badge SVG to image:', error);
    // If badge conversion fails, try without the logo in the badge
    const fallbackBadgeSVG = generateBadgeSVG(primaryColor, false, null);
    try {
      badgeImageDataURL = await svgToImage(fallbackBadgeSVG);
    } catch {
      badgeImageDataURL = '';
    }
    // Try to convert background SVG even if badge failed
    try {
      backgroundImageDataURL = await svgToImage(backgroundSVG, 192, 649);
    } catch {
      backgroundImageDataURL = '';
    }
  }

  const htmlContent = generateCertificateHTML({
    options,
    badgeImageDataURL,
    orgLogoDataURL: orgLogoDataURL || undefined,
    backgroundImageDataURL: backgroundImageDataURL || undefined,
  });

  // Create container positioned off-screen but still in viewport for html2canvas
  const container = document.createElement('div');
  container.innerHTML = htmlContent;
  container.style.cssText = `
    position: fixed;
    top: 0;
    left: -9999px;
    width: 1200px;
    height: 650px;
    z-index: -1;
    background: #ffffff;
    overflow: hidden;
  `;
  document.body.appendChild(container);

  const element = container.firstElementChild as HTMLElement;
  if (!element) {
    document.body.removeChild(container);
    throw new Error('Failed to create certificate element');
  }

  // Wait for all images to load with CORS support
  const images = container.querySelectorAll('img');
  const imagePromises = Array.from(images).map((img) => {
    return new Promise<void>((resolve) => {
      const imgElement = img;
      if (imgElement.complete && imgElement.naturalWidth > 0) {
        resolve();
        return;
      }

      const timeout = setTimeout(() => resolve(), 5000); // 5s timeout

      const handleLoad = () => {
        clearTimeout(timeout);
        resolve();
      };
      const handleError = () => {
        clearTimeout(timeout);
        resolve(); // Continue even if image fails
      };

      imgElement.onload = handleLoad;
      imgElement.onerror = handleError;

      // Force reload if needed
      if (imgElement.src && !imgElement.complete) {
        const { src } = imgElement;
        imgElement.src = '';
        imgElement.src = src;
      }
    });
  });

  await Promise.all(imagePromises);

  // Additional delay to ensure all rendering is complete
  await new Promise((r) => setTimeout(r, 500));

  // eslint-disable-next-line new-cap
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'px',
    format: [1200, 650],
    hotfixes: ['px_scaling'],
  });

  return new Promise<void>((resolve, reject) => {
    pdf.html(element, {
      callback: (doc) => {
        try {
          document.body.removeChild(container);
          const pages = doc.getNumberOfPages();
          for (let i = pages; i > 1; i--) {
            doc.deletePage(i);
          }
          const sanitizedCourseName = courseName.replace(/[^a-z0-9]/gi, '_');
          const sanitizedEmployeeName = employeeName.replace(/[^a-z0-9]/gi, '_');
          const fileName = `${sanitizedCourseName}_${sanitizedEmployeeName}_certificate.pdf`;
          doc.save(fileName);
          resolve();
        } catch (error) {
          if (container.parentNode) {
            document.body.removeChild(container);
          }
          reject(error);
        }
      },
      x: 0,
      y: 0,
      width: 1200,
      windowWidth: 1200,
      html2canvas: {
        scale: 1,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
        width: 1200,
        height: 650,
      },
    });
  });
}

export interface LegacyExportOptions {
  courseName: string;
  employeeName: string;
}

export function generatePDFFileName({ courseName, employeeName }: LegacyExportOptions): string {
  return `${courseName.replace(/[^a-z0-9]/gi, '_')}_${employeeName.replace(/[^a-z0-9]/gi, '_')}`;
}
